// Lexical-opacity scan for the predicate-head slot recognizer
// (`editorContextSlot.predicateHead.ts`) — predicate-head plan P2. Pure text
// scan (G11-rule: no tree-sitter); split out to keep the recognizer ≤200
// lines, mirroring the `editorContextSlot.valueScan.ts` split.
//
// PH-D6 refuses string / comment / dollar-quote interiors outright. Unlike the
// scalar-value slot's `scanValueTextState` (which must treat an sq-string
// interior as an in-progress VALUE operand and does not know mysql `#`
// comments or dollar quotes), a predicate head can NEVER sit inside any
// lexical container, so this scan folds every container interior — strings,
// dollar-quoted bodies, quoted identifiers, line/block comments — into one
// boolean. Engine gates (mysql `#`, postgres `$…$`, mssql/sqlite `[…]`, mysql
// `` `…` ``) mirror `tokenizeUntil` (`editorContextClause.ts`) so opacity and
// tokenization agree on where code ends.
//
// Boundary semantics mirror the shipped value-scan conditions:
//   - line comments: cursor before the terminating newline is inside; a
//     comment running to EOF keeps the EOF offset inside (unterminated);
//   - block comments / strings / quoted ids: cursor strictly inside, or AT
//     the EOF of a construct whose skip ran to EOF (the skip primitives
//     cannot distinguish "closed exactly at EOF" from "unterminated" — the
//     conservative opaque reading matches `scanValueTextState`'s).

import {
  skipBacktickId,
  skipBlockComment,
  skipBracketedId,
  skipDoubleQuoted,
  skipSingleQuoted,
  skipToEol,
  tryDollarQuoted,
} from './scanPrimitives';

/** True when `offset` falls inside a comment, string literal, dollar-quoted
 *  body, or quoted-identifier interior — positions where the predicate-head
 *  recognizer must never fire (PH-D6). */
export function isPredicateHeadOpaque(
  source: string,
  from: number,
  offset: number,
  engine: string,
): boolean {
  let i = from;
  while (i < offset) {
    const c = source.charCodeAt(i);
    if (c === 0x2d /* - */ && source.charCodeAt(i + 1) === 0x2d) {
      const end = skipToEol(source, i + 2);
      if (offset < end || end >= source.length) return true;
      i = end;
      continue;
    }
    if (engine === 'mysql' && c === 0x23 /* # */) {
      const end = skipToEol(source, i + 1);
      if (offset < end || end >= source.length) return true;
      i = end;
      continue;
    }
    if (c === 0x2f /* / */ && source.charCodeAt(i + 1) === 0x2a /* * */) {
      const end = skipBlockComment(source, i + 2);
      // C1 remediation (sol P2): a block comment is TRIVIA, not an operand, so
      // a cursor sitting immediately after a CLOSED `*/` at EOF is in code —
      // `WHERE /* note */▮` must still reach the recognizer. Only an
      // UNTERMINATED comment running to EOF keeps the cursor opaque.
      // (Strings and dollar-quotes below keep the conservative EOF posture on
      // purpose: those are operands, and a completed operand before the cursor
      // is a refusal shape anyway.)
      const terminated =
        end >= i + 4 &&
        source.charCodeAt(end - 2) === 0x2a /* * */ &&
        source.charCodeAt(end - 1) === 0x2f /* / */;
      if (offset < end || (offset === end && end === source.length && !terminated)) return true;
      i = end;
      continue;
    }
    if (c === 0x27 /* ' */) {
      const end = skipSingleQuoted(source, i + 1);
      if (offset < end || (offset === end && end === source.length)) return true;
      i = end;
      continue;
    }
    if (engine === 'postgres' && c === 0x24 /* $ */) {
      const end = tryDollarQuoted(source, i);
      if (end !== -1) {
        if (offset < end || (offset === end && end === source.length)) return true;
        i = end;
        continue;
      }
    }
    const idEnd =
      c === 0x22 /* " */ ? skipDoubleQuoted(source, i + 1)
      : (engine === 'mssql' || engine === 'sqlite') && c === 0x5b /* [ */
        ? skipBracketedId(source, i + 1)
      : engine === 'mysql' && c === 0x60 /* ` */ ? skipBacktickId(source, i + 1)
      : -1;
    if (idEnd !== -1) {
      if (offset < idEnd || (offset === idEnd && idEnd === source.length)) return true;
      i = idEnd;
      continue;
    }
    i += 1;
  }
  return false;
}
